import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import ServiceProviderService from '../services/ServiceProviderService';
import { RootState } from './store';
import { IServiceProvider, IServiceProviderRequest } from "@ontime/types";

// --- LocalStorage Helpers ---
const loadState = (): ServiceProviderState => {
    try {
        const serializedState = localStorage.getItem('serviceProviders');
        return serializedState ? JSON.parse(serializedState) : { serviceProviders: [], loading: false, error: null };
    } catch (error) {
        console.error('Could not load service providers state:', error);
        return { serviceProviders: [], loading: false, error: null };
    }
};

const saveState = (state: ServiceProviderState) => {
    try {
        localStorage.setItem('serviceProviders', JSON.stringify(state));
    } catch (error) {
        console.error('Could not save service providers state:', error);
    }
};

// --- Async Thunks ---
export const fetchServiceProviders = createAsyncThunk(
    'serviceProviders/fetchAll',
    async ({ organizationId, showDeleted }: { organizationId: string, showDeleted: boolean }) => {
        try {
            return await ServiceProviderService.GetList(organizationId, showDeleted);
        } catch (error) {
            return null;
        }
    }
);

export const getServiceProviderAsync = createAsyncThunk(
    'serviceProviders/get',
    async ({ organizationId, serviceProviderId }: { organizationId: string; serviceProviderId: string }) => {
        try {
            return await ServiceProviderService.Get(organizationId, serviceProviderId);
        } catch (error) {
            return null;
        }
    }
);

export const createServiceProviderAsync = createAsyncThunk(
    'serviceProviders/create',
    async ({ organizationId, data }: { organizationId: string; data: IServiceProviderRequest }) => {
        try {
            return await ServiceProviderService.Create(organizationId, data);
        } catch (error) {
            return null;
        }
    }
);

export const updateServiceProviderAsync = createAsyncThunk(
    'serviceProviders/update',
    async ({ organizationId, data }: { organizationId: string; data: IServiceProviderRequest }) => {
        try {
            return await ServiceProviderService.Update(organizationId, data);
        } catch (error) {
            return null;
        }
    }
);

export const deleteServiceProviderAsync = createAsyncThunk(
    'serviceProviders/delete',
    async ({ organizationId, serviceProviderId, currentIsDeleted }: { organizationId: string; serviceProviderId: string, currentIsDeleted: boolean }) => {
        try {
            return await ServiceProviderService.Delete(organizationId, serviceProviderId, currentIsDeleted);
        } catch (error) {
            return null;
        }
    }
);

// --- State Interface ---
interface ServiceProviderState {
    serviceProviders: IServiceProvider[];
    loading: boolean;
    error: string | null;
}

const initialState: ServiceProviderState = loadState();

// --- Slice ---
const serviceProviderSlice = createSlice({
    name: 'serviceProviders',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchServiceProviders.pending, (state) => {
                state.serviceProviders = [];
                state.loading = true;
                state.error = null;
                saveState(state);
            })
            .addCase(fetchServiceProviders.fulfilled, (state, action) => {
                state.loading = false;
                state.serviceProviders = action.payload ?? [];
                saveState(state);
            })
            .addCase(fetchServiceProviders.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                saveState(state);
            })
            .addCase(createServiceProviderAsync.fulfilled, (state, action) => {
                if (action.payload) {
                    state.serviceProviders.unshift(action.payload);
                    saveState(state);
                }
            })
            .addCase(updateServiceProviderAsync.fulfilled, (state, action) => {
                if (action.payload) {
                    const index = state.serviceProviders.findIndex(sp => sp.serviceProviderId === action.payload?.serviceProviderId);
                    if (index !== -1) {
                        state.serviceProviders[index] = action.payload;
                        saveState(state);
                    }
                }
            })
            .addCase(deleteServiceProviderAsync.fulfilled, (state, action) => {
                state.serviceProviders = state.serviceProviders.filter(sp => sp.serviceProviderId !== action.payload?.serviceProviderId);
                saveState(state);
            });
    }
});

export default serviceProviderSlice.reducer;
export const selectServiceProviders = (state: RootState) => state.serviceProviders.serviceProviders;
export const selectServiceProvidersLoading = (state: RootState) => state.serviceProviders.loading;
export const selectServiceProvidersError = (state: RootState) => state.serviceProviders.error;